'use client';

import 'swiper/css';
import 'swiper/css/pagination';

import type { Swiper as SwiperCore } from 'swiper';
import type { VerticalProductTabItemProps } from '@weberus/ui/components';
import type { SwiperOptions } from 'swiper/types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { Swiper, SwiperSlide } from 'swiper/react';
import { VerticalProductTabItem } from '@weberus/ui/components';
import { YaTrackerImpression, clientLogStandardized } from '@weberus/client-tracker';

import { useAtomValue } from 'jotai';
import { waitUntilClickTimeout } from '@weberus/utils';
import { pcWidthAtom } from '@/app/_shared/store/device';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { SwiperNavButton } from '../../common';

export interface ProductListProps {
  pageNameLog: HomeLogPageNameValue;
  tabItems: VerticalProductTabItemProps[][];
  widgetLogMeta: Record<string, unknown>;
  tabLogMeta: Record<string, unknown>[];
  swiperRef: React.MutableRefObject<SwiperCore | null>;
  onSlideChange: (index: number) => void;
}

export const ProductList: React.FC<ProductListProps> = ({
  pageNameLog,
  tabItems,
  widgetLogMeta,
  tabLogMeta,
  swiperRef,
  onSlideChange,
}) => {
  const { isPcWidth } = useAtomValue(pcWidthAtom);
  const swiperSetting: SwiperOptions = {
    loop: isPcWidth,
    spaceBetween: isPcWidth ? 20 : 0,
  };

  const showNavButton = tabItems.length > 1;

  const handleClientLog = (eventType: 'click' | 'impression', tIndex: number, item: VerticalProductTabItemProps) => {
    // 주의 object, objectType 속성이 각 LogMeta 에 존재하므로 최종으로 전달할 정보를 덮어씌워야 함
    return clientLogStandardized(eventType, pageNameLog, {
      ...widgetLogMeta,
      ...tabLogMeta[tIndex],
      ...item.logMeta,
    });
  };

  return (
    <div className="relative w-full pc:w-pc-size">
      <Swiper
        {...swiperSetting}
        onSwiper={(swiper: SwiperCore) => {
          swiperRef.current = swiper; // Swiper 인스턴스 저장
        }}
        onRealIndexChange={(e) => {
          onSlideChange(e.realIndex);
        }}>
        {tabItems.map((tab, tIndex) => (
          <SwiperSlide key={`${tIndex}_${isPcWidth}`}>
            <div className="items-center px-16 pc:px-0">
              <div className="grid grid-cols-2 grid-rows-2 gap-x-12 gap-y-16 pc:flex pc:flex-row pc:justify-between pc:gap-x-20 pc:gap-y-0">
                {tab.map((item, index) => (
                  <YaTrackerImpression
                    key={index}
                    {...item}
                    onImpressed={() => handleClientLog('impression', tIndex, item)}>
                    <div key={index} className="pc:w-[305px]">
                      <VerticalProductTabItem
                        key={index}
                        {...item}
                        onClick={async (e) => {
                          e.preventDefault();
                          await waitUntilClickTimeout(handleClientLog('click', tIndex, item));
                          if (item.action) navigateTo(item.action);
                        }}
                      />
                    </div>
                  </YaTrackerImpression>
                ))}
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      {!!showNavButton && <SwiperNavButton swiperRef={swiperRef} top={179} />}
    </div>
  );
};
